import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Hero = ({ products }) => {
  const featuredProduct = products[0];

  const scrollToCollections = () => {
    document.getElementById('collections')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1634113709155-5c2756711384?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Luxury timepiece"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 hero-gradient" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 pt-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-xs uppercase tracking-[0.3em] text-gold-500 font-sans"
              >
                Swiss Craftsmanship Since 1892
              </motion.p>
              <h1 className="text-5xl md:text-7xl font-serif tracking-tight leading-[0.9] text-white/90">
                Time,<br />
                <span className="italic text-gold-500">Redefined</span>
              </h1>
              <p className="text-lg text-white/60 font-light max-w-md leading-relaxed">
                Each Chronos timepiece is a masterwork of precision engineering and timeless design, crafted for those who appreciate the extraordinary.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToCollections}
                className="bg-gold-500 text-black hover:bg-gold-400 rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold transition-all hover:scale-[1.02] btn-glow"
                data-testid="explore-collection-btn"
              >
                Explore Collection
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                className="bg-transparent border border-white/20 text-white hover:border-gold-500 hover:text-gold-500 rounded-none px-8 py-6 text-sm uppercase tracking-widest transition-all"
                data-testid="heritage-btn"
              >
                Our Heritage
              </Button>
            </div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex gap-12 pt-8 border-t border-white/10"
            >
              <div>
                <p className="font-serif text-3xl text-gold-500">130+</p>
                <p className="text-xs uppercase tracking-wider text-white/40 mt-1">Years of Excellence</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-gold-500">50K+</p>
                <p className="text-xs uppercase tracking-wider text-white/40 mt-1">Timepieces Crafted</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-gold-500">12</p>
                <p className="text-xs uppercase tracking-wider text-white/40 mt-1">Master Watchmakers</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Featured Product Preview */}
          {featuredProduct && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:block relative"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-gold-500/10 to-transparent blur-3xl" />
                <img
                  src={featuredProduct.image}
                  alt={featuredProduct.name}
                  className="w-full max-w-lg mx-auto relative z-10"
                />
              </div>
              <div className="absolute bottom-8 left-8 bg-black/60 backdrop-blur-sm border border-white/10 p-6">
                <p className="text-xs uppercase tracking-wider text-gold-500 mb-2">Featured</p>
                <p className="font-serif text-xl text-white">{featuredProduct.name}</p>
                <p className="font-serif text-lg italic text-gold-400 mt-1">
                  ${featuredProduct.price.toLocaleString()}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer"
        onClick={scrollToCollections}
      >
        <span className="text-xs uppercase tracking-wider text-white/40">Scroll</span>
        <ChevronDown className="w-5 h-5 text-gold-500 animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
